import { CodeIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import CopyButton from '../../components/CopyButton';
import IconButton from '../../components/IconButton';
import Table from '../../components/Table';

import { EMPTY_CELL, formatMajorUnits, formatQuantity, positionFlags, sortPositionsByValue } from './accountDetailView';
import FlagBadges from './FlagBadges';
import RawJsonBlock from './RawJsonBlock';

import type { AdminGetFinancialAccountDetailQuery } from './__generated__/AccountDetailPanel.gql';

type Position = NonNullable<
  AdminGetFinancialAccountDetailQuery['adminGetFinancialAccountDetails'][number]['investmentAudit']
>['positions'][number];

const COLUMN_COUNT = 9;
const CELL_CLASS = 'px-2 py-1 text-xs';
const NUMERIC_CELL_CLASS = `${CELL_CLASS} text-right`;
const HEADER_CELL_CLASS = 'px-2 py-1';

interface IPositionsTableProps {
  positions: readonly Position[];
  /** The server's sum of the snapshot, shown as the total — never re-added here. */
  holdingsValue: number | null;
  /** The account's balance currency, for the total. */
  currencyCode: string | null;
}

/**
 * The positions of an account's newest holdings snapshot, largest first, with
 * each one's data-quality flags and a per-row toggle for the holding and
 * security payloads it was recorded from. Scrolls inside its own bounded
 * container so the sticky header has something to stick to.
 */
const PositionsTable = ({ positions, holdingsValue, currencyCode }: IPositionsTableProps) => {
  const [openPayloads, setOpenPayloads] = React.useState<Set<string>>(new Set());
  const sorted = React.useMemo(() => sortPositionsByValue(positions), [positions]);

  const togglePayload = (securityId: string) => {
    setOpenPayloads((prev) => {
      const next = new Set(prev);
      if (next.has(securityId)) {
        next.delete(securityId);
      } else {
        next.add(securityId);
      }
      return next;
    });
  };

  return (
    <div className="border-border-primary max-h-96 overflow-auto border">
      <Table className="table-fixed">
        <Table.Header sticky>
          <Table.Row>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-20`}>Ticker</Table.HeaderCell>
            <Table.HeaderCell className={HEADER_CELL_CLASS}>Security</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-28`}>Type</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-32 text-right`}>Qty</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-32 text-right`}>Price</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-32 text-right`}>Value</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-28 text-right`}>Cost basis</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-52`}>Flags</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-9`} />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sorted.map((position) => {
            const isOpen = openPayloads.has(position.securityId);
            const name = position.securityName ?? EMPTY_CELL;
            return (
              <React.Fragment key={position.securityId}>
                <Table.Row>
                  <Table.Cell mono className={CELL_CLASS}>
                    {position.tickerSymbol ?? EMPTY_CELL}
                  </Table.Cell>
                  <Table.Cell className={`${CELL_CLASS} truncate`} title={name}>
                    {name}
                  </Table.Cell>
                  <Table.Cell muted className={`${CELL_CLASS} truncate`}>
                    {position.securityType ?? EMPTY_CELL}
                  </Table.Cell>
                  <Table.Cell className={NUMERIC_CELL_CLASS}>{formatQuantity(position.quantity)}</Table.Cell>
                  <Table.Cell
                    className={NUMERIC_CELL_CLASS}
                    title={position.institutionPriceAsOf ? `As of ${position.institutionPriceAsOf}` : undefined}
                  >
                    {formatMajorUnits(position.institutionPrice, { ...position, isUnitPrice: true })}
                  </Table.Cell>
                  <Table.Cell className={NUMERIC_CELL_CLASS}>
                    {formatMajorUnits(position.institutionValue, position)}
                  </Table.Cell>
                  <Table.Cell className={NUMERIC_CELL_CLASS}>
                    {formatMajorUnits(position.costBasis, position)}
                  </Table.Cell>
                  <Table.Cell className={CELL_CLASS}>
                    <FlagBadges flags={positionFlags(position)} />
                  </Table.Cell>
                  <Table.Cell className={CELL_CLASS}>
                    <IconButton
                      label={isOpen ? 'Hide raw JSON' : 'Show raw JSON'}
                      aria-expanded={isOpen}
                      onClick={() => togglePayload(position.securityId)}
                    >
                      <CodeIcon className="size-3.5" />
                    </IconButton>
                  </Table.Cell>
                </Table.Row>
                {isOpen && (
                  <Table.Row className="hover:bg-transparent">
                    <Table.Cell className="bg-bg-secondary p-2" colSpan={COLUMN_COUNT}>
                      <div className="flex min-w-0 flex-col gap-2">
                        <div className="text-fg-secondary flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px]">
                          <span className="flex items-center gap-1">
                            <span className="text-fg-quaternary">security</span>
                            <span className="select-all">{position.securityId}</span>
                            <CopyButton
                              value={position.securityId}
                              label="Copy security id"
                              successMessage="Security id copied."
                            />
                          </span>
                          {position.externalSecurityId && (
                            <span className="flex items-center gap-1">
                              <span className="text-fg-quaternary">plaid</span>
                              <span className="select-all">{position.externalSecurityId}</span>
                              <CopyButton
                                value={position.externalSecurityId}
                                label="Copy Plaid security id"
                                successMessage="Plaid security id copied."
                              />
                            </span>
                          )}
                        </div>
                        <div className="grid min-w-0 grid-cols-2 gap-3">
                          <RawJsonBlock title="Holding JSON" value={position.holdingRawJson} />
                          <RawJsonBlock title="Security JSON" value={position.securityRawJson} />
                        </div>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </React.Fragment>
            );
          })}
          <Table.Row className="hover:bg-transparent">
            <Table.Cell muted className={CELL_CLASS} colSpan={5}>
              {positions.length} {positions.length === 1 ? 'position' : 'positions'}
            </Table.Cell>
            <Table.Cell className={`${NUMERIC_CELL_CLASS} font-semibold`}>
              {formatMajorUnits(holdingsValue, { isoCurrencyCode: currencyCode })}
            </Table.Cell>
            <Table.Cell className={CELL_CLASS} colSpan={3} />
          </Table.Row>
        </Table.Body>
      </Table>
    </div>
  );
};

export default PositionsTable;
